import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {v4 as uuidv4} from 'uuid';
import {FixedAssetConfigModel} from '../DataModels/fixed-asset-config-model';
import {DepreciationMethod, DepreciationScheduleEntryModel, ProrateConvention} from '../DataModels/depreciation-models';
import {FixedAssetListItemModel, FixedAssetModel} from '../DataModels/fixed-asset-model';
import {NotificationService} from './notification.service';
import {DataService} from './data.service';
import {FixedAssetGlPostingPayload} from '../DataModels/fixed-asset-posting-payload';
import {DisposalModel} from '../DataModels/depreciation-models';

// NOTE: Fake in-memory backend for development/testing. Port models easily to C# later.

@Injectable({providedIn: 'root'})
export class FixedAssetsService {
  private assets$ = new BehaviorSubject<FixedAssetModel[]>([]);
  private config$ = new BehaviorSubject<FixedAssetConfigModel>({
    defaultDepreciationMethod: DepreciationMethod.StraightLine,
    defaultUsefulLifeMonths: 60,
    // Do not seed with account Numbers. These must be real Account Id values.
    // Leave empty by default so the Config form can set true Ids via the account picker.
    defaultAssetAccountId: '',
    defaultAccumulatedDepreciationAccountId: '',
    defaultDepreciationExpenseAccountId: '',
    defaultGainOnDisposalAccountId: '',
    defaultLossOnDisposalAccountId: '',
    monthlyProrate: true,
    prorateConvention: ProrateConvention.Monthly,
    assetTagPattern: 'FA-{YYYY}-{0001}',
  });
  private assetTagCounter = 1; // simple in-memory counter for auto-numbering

  constructor(private notification: NotificationService, private api: DataService) {
    // seed with a couple of demo assets
    const seed: FixedAssetModel[] = [
      this.makeAsset('Office Desk', 1200, '2023-01-15', DepreciationMethod.StraightLine, 60, 100),
      this.makeAsset('Delivery Van', 28000, '2022-07-01', DepreciationMethod.DecliningBalance200, 72, 2000),
    ];
    // Ensure MACRS category is visible in the list by assigning a sample value
    // Using a standard label that matches the MACRS options presented in the form
    if (seed[0]) {
      seed[0].category = '10-year';
    }
    // Ensure mock data has default dates if missing to avoid null/invalid dates on initial load
    // Per requirement: Acquisition Date 1/1/2026 and In Service Date 1/10/2026
    const DEFAULT_ACQ = '2026-01-01';
    const DEFAULT_IN_SERVICE = '2026-01-10';
    seed.forEach(a => {
      if (!a.acquisitionDate || !String(a.acquisitionDate).trim()) {
        a.acquisitionDate = DEFAULT_ACQ;
      }
      if (!a.inServiceDate || !String(a.inServiceDate).trim()) {
        a.inServiceDate = DEFAULT_IN_SERVICE;
      }
    });
    seed.forEach(a => this.computeDerived(a));
    // Backfill missing asset-level GL accounts from config defaults (mock)
    const cfg = this.config$.value;
    seed.forEach(a => {
      a.assetAccountId = a.assetAccountId || cfg.defaultAssetAccountId || a.assetAccountId;
      a.accumulatedDepreciationAccountId = a.accumulatedDepreciationAccountId || cfg.defaultAccumulatedDepreciationAccountId || a.accumulatedDepreciationAccountId;
      a.depreciationExpenseAccountId = a.depreciationExpenseAccountId || cfg.defaultDepreciationExpenseAccountId || a.depreciationExpenseAccountId;
      a.gainOnDisposalAccountId = a.gainOnDisposalAccountId || cfg.defaultGainOnDisposalAccountId || a.gainOnDisposalAccountId;
      a.lossOnDisposalAccountId = a.lossOnDisposalAccountId || cfg.defaultLossOnDisposalAccountId || a.lossOnDisposalAccountId;
    });
    this.assets$.next(seed);
    // Note: persistence is handled externally by FixedAssetsPersistenceService observers (no direct DI here)
  }

  getConfig(): Observable<FixedAssetConfigModel> {
    return this.config$.asObservable();
  }

  updateConfig(cfg: FixedAssetConfigModel): Observable<FixedAssetConfigModel> {
    this.config$.next({...cfg});
    // Notify success (mock backend confirmation) with a concise summary of saved values
    const yes = $localize`:Yes@@yes:Yes`;
    const no = $localize`:No@@no:No`;
    const summaryLines: string[] = [
      `<strong>` + ($localize`:Fixed Asset Config Saved@@faConfigSaved:Saved`) + `</strong>`,
      `Method: ${cfg.defaultDepreciationMethod ?? '-'}`,
      `Useful Life: ${cfg.defaultUsefulLifeMonths ?? '-'} mo`,
      `Monthly Prorate: ${cfg.monthlyProrate ? yes : no}`,
      `Prorate Convention: ${(cfg as any).prorateConvention || 'Monthly'}`,
      `Asset Acct: ${cfg.defaultAssetAccountId || '-'}`,
      `Accum Dep Acct: ${cfg.defaultAccumulatedDepreciationAccountId || '-'}`,
      `Dep Exp Acct: ${cfg.defaultDepreciationExpenseAccountId || '-'}`,
      `Gain on Disposal Acct: ${cfg.defaultGainOnDisposalAccountId || '-'}`,
      `Loss on Disposal Acct: ${cfg.defaultLossOnDisposalAccountId || '-'}`,
    ];
    this.notification.bsSuccess(summaryLines.join('<br/>'));
    return of(cfg);
  }

  list(): Observable<FixedAssetListItemModel[]> {
    return new Observable(sub => {
      const items = this.assets$.value.map(a => ({
        id: a.id,
        name: a.name,
        assetTag: a.assetTag ? a.assetTag.replace(/[{}]/g, '') : undefined,
        category: a.category,
        // Expose organization context directly from the mock so the grid doesn't need extra lookups
        companyId: a.companyId,
        companyName: a.companyName,
        locationId: a.locationId,
        locationName: a.locationName,
        entityId: a.entityId,
        entityName: a.entityName,
        acquisitionDate: a.acquisitionDate,
        acquisitionCost: a.acquisitionCost,
        accumulatedDepreciation: a.accumulatedDepreciation,
        netBookValue: a.netBookValue,
        active: a.active,
        lastPostedDepDate: a.lastPostedDepDate,
        bookDepreciationToDate: a.bookDepreciationToDate,
      }));
      sub.next(items);
      sub.complete();
    });
  }

  /**
   * Server-backed list: call fixed-assets endpoint with query params and return raw payload.
   * No field remapping is performed here; the component binds to PascalCase fields directly.
   */
  listApi(search: {
    searchText?: string;
    fromDate?: string | Date | null;
    toDate?: string | Date | null;
    activeOnly?: boolean;
    location_id?: string;
  }, page: number, pageSize: number): Observable<any> {
    const params: string[] = [];
    try {
      if (search?.searchText) {
        params.push(`search=${encodeURIComponent(String(search.searchText))}`);
      }
      if (search?.fromDate) {
        params.push(`fromDate=${encodeURIComponent(new Date(search.fromDate).toISOString())}`);
      }
      if (search?.toDate) {
        params.push(`toDate=${encodeURIComponent(new Date(search.toDate).toISOString())}`);
      }
      if (search?.activeOnly) {
        params.push('active=true');
      }
      if (search?.location_id) {
        params.push(`location_id=${encodeURIComponent(String(search.location_id))}`);
      }
    } catch {
      // ignore formatting issues; send minimal params
    }
    params.push(`pageNumber=${Math.max(0, (page ?? 1) - 1)}`);
    params.push(`pageSize=${pageSize ?? 10}`);
    const query = params.join('&');
    return this.api.getDataWithParams('fixed-assets', query);
  }

  /**
   * Mock backend: accept a unified depreciation posting payload, display a summary,
   * and apply the posted months and last posted date to in-memory assets.
   */
  mockPostDepreciation(payload: FixedAssetGlPostingPayload): Observable<{ entryId: string; count: number; amount: number }> {
    const entryId = uuidv4();
    const detail = (payload as any)?.JournalEntry?.detail || [];
    const debitTotal = (detail || []).filter(l => (l?.debit_credit ?? 0) > 0).reduce((s, l) => s + (Number(l.amount) || 0), 0);
    const creditTotal = (detail || []).filter(l => (l?.debit_credit ?? 0) < 0).reduce((s, l) => s + (Number(l.amount) || 0), 0);
    const count = (payload as any)?.AssetUpdates?.length || 0;

    // Apply updates to in-memory assets
    const map = new Map<string, { postedMonths: string[]; lastPostedDepDate: string }>();
    ((payload as any).AssetUpdates || []).forEach((u: any) => {
      // New PascalCase keys per spec; fallback to camelCase if older payloads appear in dev
      const aid = u.AssetId ?? u.assetId;
      const months = u.PostedMonths ?? u.postedMonths ?? [];
      const last = u.LastPostedDepDate ?? u.lastPostedDepDate;
      map.set(String(aid), {postedMonths: Array.from(new Set(months || [])), lastPostedDepDate: last});
    });
    const updated = this.assets$.value.map(a => {
      const u = map.get(String(a.id));
      if (!u) {
        return a;
      }
      const mergedMonths = Array.from(new Set([...(a.postedDepMonths || []), ...u.postedMonths]));
      const copy: FixedAssetModel = {
        ...a,
        postedDepMonths: mergedMonths,
        lastPostedDepDate: u.lastPostedDepDate,
      } as FixedAssetModel;
      // recompute derived fields after posting
      this.computeDerived(copy);
      return copy;
    });
    this.assets$.next(updated);

    const summary = [
      `<strong>` + ($localize`:Depreciation Posting@@depPosting:Depreciation Posting`) + `</strong>`,
      `${$localize`:Assets posted@@assetsPosted:Assets posted`}: ${count}`,
      `${$localize`:Total Debit@@totalDebit:Total Debit`}: ${debitTotal.toFixed(2)}`,
      `${$localize`:Total Credit@@totalCredit:Total Credit`}: ${creditTotal.toFixed(2)}`,
      `${$localize`:Entry Id@@entryId:Entry Id`}: ${entryId}`,
    ].join('<br/>');
    this.notification.bsSuccess(summary);

    return of({entryId, count, amount: debitTotal});
  }

  /** Mock backend: post an asset disposal and update the model */
  mockPostAssetDisposal(args: {
    assetId: string;
    disposal: { date: string; amount: number; expenses?: number },
    payload: FixedAssetGlPostingPayload
  }): Observable<{ entryId: string; amount: number }> {
    const entryId = uuidv4();
    const assetId = String(args.assetId);
    const amount = Number(args.disposal?.amount || 0);
    const expenses = Number(args.disposal?.expenses || 0);
    const dateIso = args.disposal?.date;

    const updated = this.assets$.value.map(a => {
      if (String(a.id) !== assetId) {
        return a;
      }
      const disposal: DisposalModel = {
        disposed: true,
        disposalDate: dateIso,
        proceeds: amount,
        expenses: isNaN(expenses) ? undefined : expenses,
        entryDate: dateIso || new Date().toISOString().slice(0, 10),
        // simple mock gain/loss: proceeds - expenses - netBookValue
        gainLoss: Number(((amount - (isNaN(expenses) ? 0 : expenses)) - (a.netBookValue || 0)).toFixed(2))
      };
      const copy: FixedAssetModel = {
        ...a,
        active: false,
        disposal,
      } as FixedAssetModel;
      // recompute any derived fields if necessary
      this.computeDerived(copy);
      return copy;
    });
    this.assets$.next(updated);

    const summary = [
      `<strong>${$localize`:Asset Disposal Posting@@assetDisposalPosting:Asset Disposal Posting`}</strong>`,
      `${$localize`:Asset Id@@assetId:Asset Id`}: ${assetId}`,
      `${$localize`:Proceeds@@proceeds:Proceeds`}: ${amount.toFixed(2)}`,
      `${$localize`:Expenses@@expenses:Expenses`}: ${(isNaN(expenses) ? 0 : expenses).toFixed(2)}`,
      `${$localize`:Entry Id@@entryId:Entry Id`}: ${entryId}`,
    ].join('<br/>');
    this.notification.bsSuccess(summary);

    return of({entryId, amount});
  }

  getById(id: string): Observable<FixedAssetModel | undefined> {
    return new Observable(sub => {
      const current = this.assets$.value;
      const found = current.find(a => a.id === id);
      if (!found) {
        sub.next(undefined);
        sub.complete();
        return;
      }

      // Ensure dates exist on the item itself (not just list view)
      // and backfill missing GL account IDs from config defaults.
      // Persist any normalization back to the store.
      const needsAcq = !found.acquisitionDate || !String(found.acquisitionDate).trim();
      const needsInSvc = !found.inServiceDate || !String(found.inServiceDate).trim();
      const cfg = this.config$.value;
      const needsAssetAcct = !found.assetAccountId && !!cfg.defaultAssetAccountId;
      const needsAccumAcct = !found.accumulatedDepreciationAccountId && !!cfg.defaultAccumulatedDepreciationAccountId;
      const needsDepExpAcct = !found.depreciationExpenseAccountId && !!cfg.defaultDepreciationExpenseAccountId;
      const needsGainAcct = !found.gainOnDisposalAccountId && !!cfg.defaultGainOnDisposalAccountId;
      const needsLossAcct = !found.lossOnDisposalAccountId && !!cfg.defaultLossOnDisposalAccountId;
      if (needsAcq || needsInSvc || needsAssetAcct || needsAccumAcct || needsDepExpAcct || needsGainAcct || needsLossAcct) {
        const updated: FixedAssetModel = {
          ...found,
          acquisitionDate: needsAcq ? '2026-01-01' : found.acquisitionDate,
          inServiceDate: needsInSvc ? '2026-01-10' : found.inServiceDate,
          assetAccountId: needsAssetAcct ? cfg.defaultAssetAccountId : found.assetAccountId,
          accumulatedDepreciationAccountId: needsAccumAcct ? cfg.defaultAccumulatedDepreciationAccountId : found.accumulatedDepreciationAccountId,
          depreciationExpenseAccountId: needsDepExpAcct ? cfg.defaultDepreciationExpenseAccountId : found.depreciationExpenseAccountId,
          gainOnDisposalAccountId: needsGainAcct ? cfg.defaultGainOnDisposalAccountId : found.gainOnDisposalAccountId,
          lossOnDisposalAccountId: needsLossAcct ? cfg.defaultLossOnDisposalAccountId : found.lossOnDisposalAccountId,
        } as FixedAssetModel;
        // Recompute derived fields/schedule after date backfill
        this.computeDerived(updated);
        const idx = current.findIndex(a => a.id === id);
        const arr = [...current];
        if (idx >= 0) {
          arr[idx] = updated;
        } else {
          arr.unshift(updated);
        }
        this.assets$.next(arr);
        sub.next(updated);
        sub.complete();
        return;
      }

      sub.next(found);
      sub.complete();
    });
  }

  create(model: FixedAssetModel): Observable<FixedAssetModel> {
    const asset = {...model};
    if (!asset.id) {
      asset.id = uuidv4();
    }
    // Sanitize manually entered tag if present (remove any braces)
    if (asset.assetTag) {
      asset.assetTag = asset.assetTag.replace(/[{}]/g, '');
    }
    // Auto-number Asset Tag if empty and a pattern exists
    if (!asset.assetTag) {
      const pat = (this.config$.value as any)?.assetTagPattern as string | undefined;
      if (pat) {
        asset.assetTag = this.generateAssetTag(pat);
      }
    }
    // Apply mock defaults for dates if missing/empty so schedules and reports have valid dates
    if (!asset.acquisitionDate || !String(asset.acquisitionDate).trim()) {
      asset.acquisitionDate = '2026-01-01';
    }
    if (!asset.inServiceDate || !String(asset.inServiceDate).trim()) {
      asset.inServiceDate = '2026-01-10';
    }
    // Backfill GL accounts from config defaults if missing
    const cfg = this.config$.value;
    asset.assetAccountId = asset.assetAccountId || cfg.defaultAssetAccountId || asset.assetAccountId;
    asset.accumulatedDepreciationAccountId = asset.accumulatedDepreciationAccountId || cfg.defaultAccumulatedDepreciationAccountId || asset.accumulatedDepreciationAccountId;
    asset.depreciationExpenseAccountId = asset.depreciationExpenseAccountId || cfg.defaultDepreciationExpenseAccountId || asset.depreciationExpenseAccountId;
    asset.gainOnDisposalAccountId = asset.gainOnDisposalAccountId || cfg.defaultGainOnDisposalAccountId || asset.gainOnDisposalAccountId;
    asset.lossOnDisposalAccountId = asset.lossOnDisposalAccountId || cfg.defaultLossOnDisposalAccountId || asset.lossOnDisposalAccountId;
    this.computeDerived(asset);
    this.assets$.next([asset, ...this.assets$.value]);
    // Notify success for asset create (mock backend confirmation) with brief details
    const msgCreate = [
      `<strong>` + ($localize`:Fixed Asset Saved@@faSaved:Saved`) + `</strong>`,
      `Name: ${asset.name}`,
      `Cost: ${asset.acquisitionCost}`,
      `Method: ${asset.depreciationMethod}`,
      `Life: ${asset.usefulLifeMonths} mo`,
    ].join('<br/>');
    this.notification.bsSuccess(msgCreate);
    return of(asset);
  }

  update(model: FixedAssetModel): Observable<FixedAssetModel> {
    const idx = this.assets$.value.findIndex(a => a.id === model.id);
    const copy = {...model};
    if (copy.assetTag) {
      copy.assetTag = copy.assetTag.replace(/[{}]/g, '');
    }
    // Apply mock defaults for dates if missing/empty
    if (!copy.acquisitionDate || !String(copy.acquisitionDate).trim()) {
      copy.acquisitionDate = '2026-01-01';
    }
    if (!copy.inServiceDate || !String(copy.inServiceDate).trim()) {
      copy.inServiceDate = '2026-01-10';
    }
    // Backfill GL accounts from config defaults if missing
    const cfg = this.config$.value;
    copy.assetAccountId = copy.assetAccountId || cfg.defaultAssetAccountId || copy.assetAccountId;
    copy.accumulatedDepreciationAccountId = copy.accumulatedDepreciationAccountId || cfg.defaultAccumulatedDepreciationAccountId || copy.accumulatedDepreciationAccountId;
    copy.depreciationExpenseAccountId = copy.depreciationExpenseAccountId || cfg.defaultDepreciationExpenseAccountId || copy.depreciationExpenseAccountId;
    copy.gainOnDisposalAccountId = copy.gainOnDisposalAccountId || cfg.defaultGainOnDisposalAccountId || copy.gainOnDisposalAccountId;
    copy.lossOnDisposalAccountId = copy.lossOnDisposalAccountId || cfg.defaultLossOnDisposalAccountId || copy.lossOnDisposalAccountId;
    this.computeDerived(copy);
    const arr = [...this.assets$.value];
    if (idx >= 0) {
      arr[idx] = copy;
    } else {
      arr.unshift(copy);
    }
    this.assets$.next(arr);
    // Notify success for asset update (mock backend confirmation) with brief details
    const msgUpdate = [
      `<strong>` + ($localize`:Fixed Asset Saved@@faSaved:Saved`) + `</strong>`,
      `Name: ${copy.name}`,
      `Cost: ${copy.acquisitionCost}`,
      `Method: ${copy.depreciationMethod}`,
      `Life: ${copy.usefulLifeMonths} mo`,
    ].join('<br/>');
    this.notification.bsSuccess(msgUpdate);
    return of(copy);
  }

  delete(id: string): Observable<boolean> {
    this.assets$.next(this.assets$.value.filter(a => a.id !== id));
    return of(true);
  }

  // Disposal handling: compute gain/loss and mark disposed
  dispose(id: string, disposalDate: string, proceeds: number): Observable<FixedAssetModel | undefined> {
    const asset = this.assets$.value.find(a => a.id === id);
    if (!asset) {
      return of(undefined);
    }
    const nbv = asset.netBookValue;
    const gainLoss = +(proceeds - nbv).toFixed(2);
    const updated: FixedAssetModel = {
      ...asset,
      active: false,
      disposal: {disposed: true, disposalDate, proceeds, gainLoss},
    };
    this.computeDerived(updated); // to ensure schedule/values consistent
    return this.update(updated);
  }

  private makeAsset(name: string, cost: number, acq: string, method: DepreciationMethod, lifeMonths: number, salvage: number): FixedAssetModel {
    const asset: FixedAssetModel = {
      id: uuidv4(),
      name,
      acquisitionCost: cost,
      acquisitionDate: acq,
      salvageValue: salvage,
      usefulLifeMonths: lifeMonths,
      depreciationMethod: method,
      depreciationRate: method === DepreciationMethod.DecliningBalance200 ? 2.0 : method === DepreciationMethod.DecliningBalance150 ? 1.5 : undefined,
      accumulatedDepreciation: 0,
      netBookValue: cost,
      active: true,
      postedDepMonths: [],
      // Provide mock organization context (API will later return both Id and Name)
      companyId: 'C-001',
      companyName: 'Demo Company',
      locationId: 'L-001',
      locationName: 'HQ',
      entityId: 'E-001',
      entityName: 'Default Entity',
    } as FixedAssetModel;

    // Ensure seed/mock assets have an asset tag so lists can display the Tag column
    try {
      const pat = (this.config$.value as any)?.assetTagPattern as string | undefined;
      if (pat) {
        asset.assetTag = this.generateAssetTag(pat);
      }
    } catch {
      // ignore if config not ready; tag is optional
    }

    return asset;
  }

  private computeDerived(asset: FixedAssetModel): void {
    // recompute schedule, accumulated, nbv
    const schedule = this.buildSchedule(asset);
    asset.schedule = schedule;
    if (schedule.length > 0) {
      const last = schedule[schedule.length - 1];
      asset.accumulatedDepreciation = +last.accumulatedDepreciation.toFixed(2);
      asset.netBookValue = +last.endingBookValue.toFixed(2);
    } else {
      asset.accumulatedDepreciation = 0;
      asset.netBookValue = asset.acquisitionCost;
    }

    // Compute Book Depreciation to Date based on posted months (as of last posted period)
    const postedSet = new Set(asset.postedDepMonths || []);
    if (schedule.length && postedSet.size) {
      let sum = 0;
      for (const e of schedule) {
        const key = this.ymKey(new Date(e.periodStart));
        if (postedSet.has(key)) {
          sum += Number(e.depreciationAmount) || 0;
        }
      }
      asset.bookDepreciationToDate = +sum.toFixed(2);
    } else {
      asset.bookDepreciationToDate = 0;
    }
  }

  private buildSchedule(asset: FixedAssetModel): DepreciationScheduleEntryModel[] {
    // very simplified monthly schedule for demo; GAAP-compliant methods outline
    const schedule: DepreciationScheduleEntryModel[] = [];
    // If method is None, skip building schedule
    if (asset.depreciationMethod === DepreciationMethod.None) {
      return schedule;
    }
    const life = Math.max(1, Math.floor(asset.usefulLifeMonths || 1));
    const basis = Math.max(0, asset.acquisitionCost - (asset.salvageValue || 0));
    let acc = 0;
    let remaining = basis;

    const start = new Date(asset.inServiceDate || asset.acquisitionDate);
    for (let i = 1; i <= life; i++) {
      const periodStart = new Date(start);
      periodStart.setMonth(start.getMonth() + (i - 1));
      const periodEnd = new Date(periodStart);
      periodEnd.setMonth(periodStart.getMonth() + 1);

      let dep = 0;
      switch (asset.depreciationMethod) {
        case DepreciationMethod.StraightLine: {
          dep = basis / life;
          break;
        }
        case DepreciationMethod.DecliningBalance200:
        case DepreciationMethod.DecliningBalance150: {
          const rate = (asset.depreciationRate || (asset.depreciationMethod === DepreciationMethod.DecliningBalance200 ? 2.0 : 1.5)) / (asset.usefulLifeMonths / 12);
          dep = remaining * (rate / 12);
          // avoid going below salvage
          if (remaining - dep < 0) {
            dep = remaining;
          }
          break;
        }
        case DepreciationMethod.SumOfYearsDigits: {
          const years = Math.max(1, Math.round(asset.usefulLifeMonths / 12));
          const sum = (years * (years + 1)) / 2;
          const currentYear = Math.ceil(i / 12);
          const yearFactor = (years - currentYear + 1) / sum;
          dep = (basis * yearFactor) / 12;
          break;
        }
        case DepreciationMethod.UnitsOfProduction: {
          const totalUnits = Math.max(1, asset.totalUnits || 1);
          const perUnit = basis / totalUnits;
          // naive evenly spread monthly units if none provided
          const monthlyUnits = (asset.totalUnits || totalUnits) / life;
          dep = perUnit * monthlyUnits;
          break;
        }
        default:
          dep = 0;
      }

      // ensure we never depreciate below salvage value
      if (acc + dep > basis) {
        dep = basis - acc;
      }

      acc += dep;
      remaining = basis - acc;

      schedule.push({
        periodStart: periodStart.toISOString(),
        periodEnd: periodEnd.toISOString(),
        periodNumber: i,
        depreciationAmount: +dep.toFixed(2),
        accumulatedDepreciation: +acc.toFixed(2),
        endingBookValue: +(asset.acquisitionCost - acc).toFixed(2),
      });

      // stop if fully depreciated
      if (acc >= basis) {
        break;
      }
    }

    return schedule;
  }

  /** Build YYYY-MM key */
  private ymKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }

  /** Generate an asset tag using a simple tokenized pattern */
  private generateAssetTag(pattern: string): string {
    const now = new Date();
    const yyyy = String(now.getFullYear());
    const yy = yyyy.slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    let out = pattern
      .replaceAll('{YYYY}', yyyy)
      .replaceAll('{YY}', yy)
      .replaceAll('{MM}', mm)
      .replaceAll('{DD}', dd);
    // Handle a numeric counter placeholder like {0001} or {00000}
    const counterMatch = out.match(/\{0+}/);
    if (counterMatch) {
      const zeros = counterMatch[0];
      const width = zeros.length - 2; // exclude braces
      const seq = String(this.assetTagCounter++).padStart(Math.max(1, width), '0');
      out = out.replace(counterMatch[0], seq);
    }
    // Remove any remaining braces defensively
    return out.replace(/[{}]/g, '');
  }
}
